import * as React from 'react';
import { useHistory } from 'react-router';
import { useAcquireAddressState } from '../components/AcquireAddressStateProvider';
import {
  useAddressGeopoint,
  useAddressStreetviewData,
  useAddressToTheDoorData,
} from '../components/AddressStateProvider';
import { okhiTracker } from '../OkHiTracker';
import {
  RequiredFieldsConfig,
  StreetviewData,
  ToTheDoorData,
  TrackingData,
} from '../types';
import { fetchStreetViewPano, validateInlineToPortalMessage } from '../utils';
import { useOkHiMessageSender } from './useOkHiMessageSender';

interface StyleSettings {
  primaryColor: string;
  highlightColor: string;
}

interface MessageContext {
  context: {
    isStreetviewEnabled: boolean;
    isToTheDoorEnabled: boolean;
    isDarkMode: boolean;
    styleSettings: StyleSettings;
    companyName: string;
    requiredFieldsConfig: RequiredFieldsConfig;
  };
}

interface ImpreciseGpsMessage {
  type: 'atAddressImpreciseGps';
  geoPoint: { lat: number; lng: number };
  geoPointAccuracy: number;
}

export interface PreciseGpsMessage {
  type: 'atAddressPreciseGps';
  geoPoint: { lat: number; lng: number };
  geoPointAccuracy: number;
}

interface FailedToAcquireGeolocMessage {
  type: 'failedToAcquireGeoloc';
}

interface NotAtAddressMessage {
  type: 'notAtAddress';
}

interface FindOnMapMessage {
  type: 'findOnMap';
  geoPoint: google.maps.LatLngLiteral;
  streetviewData?: StreetviewData;
  toTheDoorData?: ToTheDoorData;
}

interface DataTrackingMessage {
  type: 'trackingData';
  data: TrackingData;
}

interface DeviceIdMessage {
  type: 'deviceId';
  deviceId: string;
}

type OkHiInlineToPortalMessagePayload =
  | ImpreciseGpsMessage
  | PreciseGpsMessage
  | FailedToAcquireGeolocMessage
  | FindOnMapMessage
  | NotAtAddressMessage
  | DataTrackingMessage
  | DeviceIdMessage;

export interface OkHiInlineToPortalMessage {
  source: 'okhi';
  payload: OkHiInlineToPortalMessagePayload & MessageContext;
}

export function useOkHiMessageReceiver() {
  const [okhiMessage, setOkHiMessage] =
    React.useState<OkHiInlineToPortalMessage | null>(null);
  const history = useHistory();
  const { openPortal } = useOkHiMessageSender();
  const { setAcquireAddressState } = useAcquireAddressState();
  const { setAddressGeoPoint } = useAddressGeopoint();
  const { setAddressStreetviewData } = useAddressStreetviewData();
  const { setAddressToTheDoorData } = useAddressToTheDoorData();
  React.useEffect(() => {
    const handleOkHiMessage = async (event: MessageEvent<any>) => {
      let message: OkHiInlineToPortalMessage | undefined;
      try {
        if (typeof event.data === 'string') {
          message = JSON.parse(event?.data);
        }
      } catch (err) {
        console.error('Error parsing window message');
        return;
      }
      if (message && validateInlineToPortalMessage(message)) {
        const messageType = message?.payload.type;
        if (messageType === 'trackingData') {
          okhiTracker.enrich(
            message.payload.data,
            message.payload.context.isStreetviewEnabled,
            message.payload.context.isToTheDoorEnabled
          );
          return;
        }
        if (messageType === 'deviceId') {
          localStorage.setItem('okhi-device-id', message.payload.deviceId);
          return;
        }
        setAcquireAddressState('incomplete');
        setOkHiMessage(message);
        if (
          messageType === 'failedToAcquireGeoloc' ||
          messageType === 'notAtAddress'
        ) {
          history.push('/place-input');
          openPortal();
        } else if (messageType === 'atAddressImpreciseGps') {
          setAddressGeoPoint((message.payload as ImpreciseGpsMessage).geoPoint);
          history.push('/map');
          openPortal();
        } else if (messageType === 'atAddressPreciseGps') {
          const payload = message.payload as PreciseGpsMessage & MessageContext;
          setAddressGeoPoint(payload.geoPoint);
          if (payload.context.isStreetviewEnabled) {
            try {
              const streetviewData = await fetchStreetViewPano(
                payload.geoPoint.lat,
                payload.geoPoint.lng
              );
              const panoId = streetviewData.location?.pano;
              if (panoId != null) {
                setAddressStreetviewData({ panoId });
                history.push('/streetview');
                openPortal();
                return;
              }
            } catch (error) {
              console.error(error);
              console.error('failed to fetch streetview panorama');
            }
          }
          if (payload.context.isToTheDoorEnabled) {
            history.push('/to-the-door');
            openPortal();
          } else {
            history.push('/loading');
            // We don't want to complete here because we want to wait for road name suggestions to finish loading.(Not blocking anything)
            setAcquireAddressState('waitForRoadNames');
            /* try {
              const placeSuggestions = await fetchPlaceSuggestions(
                payload.geoPoint
              );
              sendMessage({
                type: "addressMessage",
                geoPoint: payload.geoPoint,
                streetviewData: null,
                toTheDoorData: null,
                placeSuggestions,
              });
            } catch (error) {
              sendMessage({ type: "error" });
            } */
          }
        } else if (messageType === 'findOnMap') {
          setAddressGeoPoint(message.payload.geoPoint);
          if (message.payload.streetviewData) {
            setAddressStreetviewData(message.payload.streetviewData);
          }
          if (message.payload.toTheDoorData) {
            setAddressToTheDoorData(message.payload.toTheDoorData);
          }
          history.push('/map');
          openPortal();
        }
      }
    };
    window.addEventListener('message', handleOkHiMessage);
    return () => {
      window.removeEventListener('message', handleOkHiMessage);
    };
  }, [
    history,
    setAddressGeoPoint,
    setAddressStreetviewData,
    setAddressToTheDoorData,
    setAcquireAddressState,
    openPortal,
  ]);

  return okhiMessage;
}
